import {
  FilterOperator,
  type SearchQSetsQuery,
  useExecuteQSetCheckSimilarityLazyQuery,
  useSearchQSetsLazyQuery,
  type ExecuteQSetCheckSimilarityQuery,
} from '@exam-management/graphql';
import { useLogger } from '@shared/logger';
import { useCallback } from 'react';

import { getQSetsDraftAndPublishedDetails } from '../../utils/get-qsets-draft-and-published-details';

type SimilarityResult = ExecuteQSetCheckSimilarityQuery['excGetQSetSimilarities'];
type QSet = SearchQSetsQuery['excSearchQSets']['qSets'][number];

export default function useQSetSimilarityCheck() {
  const logger = useLogger();

  const [searchQSets] = useSearchQSetsLazyQuery();
  const [executeQSetCheckSimilarity] = useExecuteQSetCheckSimilarityLazyQuery();

  const execute = useCallback(
    async (
      qSetIds: string[],
      options: {
        onCompleted?: (data: SimilarityResult, draftQSets: QSet[], publishedQSets: QSet[]) => void;
        onError?: () => void;
      },
    ) => {
      const { data: searchedQSets } = await searchQSets({
        fetchPolicy: 'cache-and-network',
        variables: {
          searchQSetsInput: {
            paging: {
              // NOTE: page size is set to 200 to get all qsets
              pageSize: 200,
              currentPage: 0,
            },
            filter: {
              conditions: [
                {
                  field: 'id',
                  operator: FilterOperator.In,
                  value: qSetIds,
                },
              ],
            },
          },
        },
      });

      if (!searchedQSets?.excSearchQSets.qSets) return;

      const { draftQSets, publishedQSets } = getQSetsDraftAndPublishedDetails(
        searchedQSets.excSearchQSets.qSets,
      );

      const { data } = await executeQSetCheckSimilarity({
        fetchPolicy: 'cache-first',
        variables: {
          input: {
            qSetsA: (searchedQSets?.excSearchQSets.qSets ?? []).map((qSet) => ({
              id: qSet.id,
              code: qSet.code,
              updatedAt: qSet.updatedAt,
            })),
          },
        },
        onError(error) {
          logger.error(error);
          options?.onError?.();
        },
      });

      if (data?.excGetQSetSimilarities) {
        // When the api is cached, on completed will not trigger
        options?.onCompleted?.(data.excGetQSetSimilarities, draftQSets, publishedQSets);
      }
    },
    [logger, executeQSetCheckSimilarity, searchQSets],
  );

  return [execute] as const;
}
